import { ReviewResponse, CoverLetterResponse, InterviewPrepResponse, CommunicationsResponse, RewriteResumeResponse } from '@/api/types';
import { analyzeResume as genAnalyzeResume, generateCoverLetter as genCoverLetter, generateInterviewPrep as genInterviewPrep, generateCommunications as genCommunications, generateRefinedResume as genRewriteResume } from '@/services/geminiService';
import { getResumes } from './resumes';

export const review = async (workspaceId: string, payload: {
  resumeId: string;
  jobText: string;
  company: string;
  position: string;
  requirements: string;
  targetLocation: string;
  jobType?: string;
  workModel?: string;
  visaSponsorship?: string;
  relocation?: string;
}, options?: { signal?: AbortSignal }): Promise<ReviewResponse> => {
  const resumesRes = await getResumes(workspaceId);
  const resume = resumesRes.items.find(r => r.id === payload.resumeId);
  if (!resume) throw new Error('Resume not found');
  
  return genAnalyzeResume(
    resume,
    payload.jobText,
    payload.company,
    payload.position,
    payload.requirements,
    payload.targetLocation,
    payload.jobType,
    payload.workModel,
    payload.visaSponsorship,
    payload.relocation,
    options?.signal
  );
};

export const generateCoverLetter = async (workspaceId: string, payload: {
  resumeId: string;
  jobText: string;
  company: string;
  position: string;
  requirements: string;
  targetLocation: string;
  jobType?: string;
  workModel?: string;
  visaSponsorship?: string;
  relocation?: string;
}, options?: { signal?: AbortSignal }): Promise<CoverLetterResponse> => {
  const resumesRes = await getResumes(workspaceId);
  const resume = resumesRes.items.find(r => r.id === payload.resumeId);
  if (!resume) throw new Error('Resume not found');
  
  return genCoverLetter(
    resume,
    payload.jobText,
    payload.company,
    payload.position,
    payload.requirements,
    payload.targetLocation,
    payload.jobType,
    payload.workModel,
    payload.visaSponsorship,
    payload.relocation,
    options?.signal
  );
};

export const generateInterviewPrep = async (workspaceId: string, payload: {
  resumeId: string;
  jobText: string;
  company: string;
  position: string;
}, options?: { signal?: AbortSignal }): Promise<InterviewPrepResponse> => {
  const resumesRes = await getResumes(workspaceId);
  const resume = resumesRes.items.find(r => r.id === payload.resumeId);
  if (!resume) throw new Error('Resume not found');
  
  return genInterviewPrep(resume, payload.jobText, payload.company, payload.position, options?.signal);
};

export const generateCommunications = async (workspaceId: string, payload: {
  resumeId: string;
  jobText: string;
  emailType?: string;
  company: string;
  position: string;
}, options?: { signal?: AbortSignal }): Promise<CommunicationsResponse> => {
  const resumesRes = await getResumes(workspaceId);
  const resume = resumesRes.items.find(r => r.id === payload.resumeId);
  if (!resume) throw new Error('Resume not found');
  
  return genCommunications(resume, payload.jobText, payload.company, payload.position, payload.emailType, options?.signal);
};

export const rewriteResume = async (workspaceId: string, payload: {
  resumeId: string;
  jobText: string;
  recommendations: any[];
  customInstructions?: string;
}, options?: { signal?: AbortSignal }): Promise<RewriteResumeResponse> => {
  const resumesRes = await getResumes(workspaceId);
  const resume = resumesRes.items.find(r => r.id === payload.resumeId);
  if (!resume) throw new Error('Resume not found');
  
  let extractedUrlsText = '';
  if (resume.contentBase64) {
    try {
      const { pdfjs } = await import('react-pdf');
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      
      const binaryString = window.atob(resume.contentBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
      const extractedLinks: { url: string; context: string }[] = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const annotations = await page.getAnnotations();
        const textContent = await page.getTextContent();
        
        annotations.forEach((anno: any) => {
          if (anno.subtype === 'Link' && anno.url) {
            let contextText = '';
            if (anno.rect) {
              const [x1, y1, x2, y2] = anno.rect;
              const middleY = (y1 + y2) / 2;
              
              const sameLineItems = textContent.items.filter((item: any) => {
                if (item.transform && item.transform.length >= 6) {
                  const itemY = item.transform[5];
                  return Math.abs(itemY - middleY) < 15;
                }
                return false;
              });
              sameLineItems.sort((a: any, b: any) => a.transform[4] - b.transform[4]);
              contextText = sameLineItems.map((c: any) => c.str).join('').replace(/\s+/g, ' ').substring(0, 150).trim();
            }
            extractedLinks.push({ url: anno.url, context: contextText });
          }
        });
      }
      if (extractedLinks.length > 0) {
        const formattedLinks = extractedLinks.map((l) => `- URL: ${l.url} (Found near text: "${l.context}")`).join('\n');
        extractedUrlsText = `\nCRITICAL: The following hidden URLs were extracted from the PDF WITH their surrounding text. You MUST assign them to the correct header and project by matching the surrounding text to the project name. Ensure NO URL is lost:\n${formattedLinks}\n\nINSTRUCTION: In your response JSON, you MUST fill out the 'url' field for EVERY project that has a corresponding GitHub or deployment URL listed above! Do NOT leave project URLs blank if they are present!`;
      }
    } catch (e) {}
  }

  const modifiedCustomInstructions = extractedUrlsText 
    ? `${extractedUrlsText}\nUser Instructions:\n${payload.customInstructions || ''}`
    : payload.customInstructions;

  return genRewriteResume(resume, payload.jobText, payload.recommendations, modifiedCustomInstructions, undefined, options?.signal);
};

export const extractJobMetadata = async (jobText: string) => {
  const { extractJobMetadata: extract } = await import('@/services/geminiService');
  return extract(jobText);
};
